import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PiPlusBold } from 'react-icons/pi';
import toast from 'react-hot-toast';
import { Button, LoadingState, PageHeader } from '@v2/design-system';
import api from '@v2/lib/api';
import EditRoleModal from '@v2/features/users/EditRoleModal';
import RoleCard, { type RoleCardData } from '@v2/features/users/RoleCard';
import UsersAdminTabs from '@v2/features/users/UsersAdminTabs';

type RolesResponse = {
    roles: RoleCardData[];
};

export default function RolesPermissionsPage() {
    const qc = useQueryClient();
    const [modal, setModal] = useState<{ mode: 'create' | 'edit'; roleId: number | null } | null>(null);

    const rolesQuery = useQuery<RolesResponse>({
        queryKey: ['roles'],
        queryFn: async () => (await api.get('/roles')).data,
    });

    const deleteMutation = useMutation({
        mutationFn: async (id: number) => api.delete(`/roles/${id}`),
        onSuccess: () => {
            toast.success('Função removida.');
            qc.invalidateQueries({ queryKey: ['roles'] });
        },
        onError: (err: { response?: { data?: { message?: string } } }) => {
            toast.error(err?.response?.data?.message ?? 'Não foi possível excluir.');
        },
    });

    const roles = rolesQuery.data?.roles ?? [];

    if (rolesQuery.isLoading) {
        return <LoadingState label="Carregando funções…" />;
    }

    if (rolesQuery.isError) {
        return (
            <div>
                <PageHeader
                    title="Funções e permissões"
                    breadcrumb={[{ name: 'Dashboard', href: '/' }, { name: 'Administração' }, { name: 'Funções e permissões' }]}
                />
                <UsersAdminTabs />
                <p className="text-sm text-rose-600">
                    Sem permissão para gerenciar funções ou erro ao carregar.
                </p>
            </div>
        );
    }

    return (
        <div>
            <PageHeader
                title="Funções e permissões"
                breadcrumb={[
                    { name: 'Dashboard', href: '/' },
                    { name: 'Administração' },
                    { name: 'Funções e permissões' },
                ]}
            >
                <Button
                    type="button"
                    className="bg-gray-900 text-white hover:bg-gray-800"
                    onClick={() => setModal({ mode: 'create', roleId: null })}
                >
                    <PiPlusBold className="me-1.5 size-4" />
                    Nova função
                </Button>
            </PageHeader>

            <UsersAdminTabs />

            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
                {roles.map((role) => (
                    <RoleCard
                        key={role.id}
                        role={role}
                        onEdit={() => setModal({ mode: 'edit', roleId: role.id })}
                        onDelete={
                            !role.is_protected
                                ? () => {
                                      if (confirm(`Excluir a função "${role.label}"?`)) {
                                          deleteMutation.mutate(role.id);
                                      }
                                  }
                                : undefined
                        }
                    />
                ))}
            </div>

            {roles.length === 0 && (
                <p className="py-12 text-center text-sm text-gray-500">Nenhuma função cadastrada.</p>
            )}

            {modal && (
                <EditRoleModal
                    open
                    mode={modal.mode}
                    roleId={modal.roleId}
                    onClose={() => setModal(null)}
                    onSaved={() => qc.invalidateQueries({ queryKey: ['roles'] })}
                />
            )}
        </div>
    );
}
